import { useCallback, useEffect, useRef, useState } from "react";
import SntDialog from "../SntDialog/SntDialog";
import { ReactSortable } from "react-sortablejs";
import SntCheckBox from "@cleanup/components/SntCheckBox/SntCheckBox";
import { useSelector } from "react-redux";
import { Col, Form, Row } from "react-bootstrap";
import { LanguageKey } from "@/types/generated/LanguageKey";
import { FeatureKey } from "@/types/generated/FeatureKey";
import { translate } from "@/hooks/useLanguage";
import { hasFeature } from "@/hooks/useFeatures";
import { RootState } from "@/types/RootState";
import SntReorderIcon from "@icons/SntReorderIcon";
import SntLink from "../ReactBootstrap/SntLink";
import SntCloseIcon from "@icons/SntCloseIcon";
import IcomoonIcon from "@icons/IcomoonIcon";
import styled from "styled-components";
import SntGroupBy from "@icons/SntGroupBy";
import SntUnGroup from "@icons/SntUnGroup";
import SntSortZA from "@icons/SntSortZA";
import SntSortAZ from "@icons/SntSortAZ";
import { VerticalLayout } from "@/cleanup/components/FormValidation";
import SntRadio from "@cleanup/components/SntRadio/SntRadio.tsx";
import {
  bucketTypeConstant,
  supportedGroupTypeConstant,
} from "@/components/SntTableViewCard/SntTableConfigurationConstant";
import SntSelect from "@wrappers/ReactSelect/SntSelect";
import { getGroupColumnData } from "@/cleanup/utils/tableUtils";
import { ColumnDefinition } from "@/types/ColumnTypes";
import SntButton from "@/cleanup/widgets/forms/SntButton";
import SensolusOnlyBadge from "@/cleanup/widgets/SensolusOnlyBadge";

const ConfigLayout = styled.table`
  width: 100%;
  margin-bottom: 0px;

  tr td:first-child {
    width: 30%;
    padding-right: 1rem;
  }

  .list-group {
    width: 100%;
  }

  .list-group-item-selected {
    background: #eff3f4;
    padding: 0.3rem 0.5rem;
    margin-bottom: 0.25rem;
    border-radius: 4px;
    &.group {
      background: white;
      border: 1px solid var(--bs-list-group-border-color);
    }
  }
  .list-group-item-unselected {
    border-radius: 0;
    border-bottom: 1px solid var(--bs-list-group-border-color);
  }

  .list-group-item-selected .sortIconHover {
    opacity: 0;
  }

  .list-group-item-selected:hover {
    .sortIconHover {
      opacity: 1;
    }
  }

  .column-scroll {
    overflow: hidden auto;
  }

  .hide-long-title {
    white-space: nowrap !important;
    overflow: hidden !important;
    text-overflow: ellipsis !important;
  }
`;

/* eslint-disable @typescript-eslint/no-explicit-any */
interface ConfigState {
  selectedColumns: any[];
  selectedGroup: any[];
  selectedBucket: string | null;
  selectedAggregateMetrics: any[];
  bucketConfig: Record<string, any>;
}

interface ColumnCategory {
  key: string;
  columns: ColumnDefinition[];
  groupColumns: ColumnDefinition[];
  category?: string;
}


interface SntConfigColumnsProps {
  dataFilter: {
    filter: any;
    allColumns: ColumnDefinition[];
    defaultVisibleColumnKeys: string[];
    /* eslint-disable @typescript-eslint/no-explicit-any */
    setColumnsSetting: (list: any[], sortBy?: any, groupKeys?: any[], selectedBucket?: any, bucketConfig?: any, metrics?: any[]) => void;
    
  };
  isShowConfigDialog: boolean;
  onDone: () => void;
  onCancel: () => void;
  isDisabbledGroup?: boolean;
  isDisabbledUnGroupIcon?: boolean;
  optionsSettings?: {
    query?: any[];
  };
  defaultSortBy?: [string, boolean];
  isHiddenSupportedMetrics?: boolean;
  // Hide the A-Z/Z-A sort icons, for contexts where the chosen sort is not
  // applied (e.g. report column selection — see STIC-15282).
  isHiddenSortIcons?: boolean;
}

export const SntConfigColumns = ({
  dataFilter,
  isShowConfigDialog,
  onDone,
  onCancel,
  isDisabbledGroup,
  isDisabbledUnGroupIcon = false,
  optionsSettings,
  defaultSortBy,
  isHiddenSupportedMetrics,
  isHiddenSortIcons = false,
}: SntConfigColumnsProps) => {
  
  const { isMobile } = useSelector((state: RootState) => state.all);

  const {
    filter,
    allColumns,
    defaultVisibleColumnKeys,
    setColumnsSetting,
  } = dataFilter;

  const [configState, setConfigState] = useState<ConfigState | null>(null);
  const [columnCategories, setColumnCategories] = useState<ColumnCategory[]>([]);
  const [columnsByKey, setColumnsByKey] = useState<Record<string, ColumnDefinition>>({});

  const [sortBy, setSortBy] = useState<{key: string; desc?: boolean} | undefined>();
  const [searchColumn, setSearchColumn] = useState("");

  const [filterQuery, setFilterQuery] = useState<any[]>([]);
  useEffect(() => {
    if (optionsSettings?.query) {
      setFilterQuery(optionsSettings?.query);
    }
  }, [optionsSettings]);

  const initializeCategories = useCallback((allColumns: ColumnDefinition[]) => {
    const groupByKey: Record<string, ColumnCategory> = {},
      groupList: ColumnCategory[] = [],
      columnMap: Record<string, ColumnDefinition> = {};

    for (let i = 0; i < allColumns.length; i++) {
      const obj = allColumns[i];
      // skip immovables
      if (obj.immovable) {
        continue;
      }
      columnMap[obj.columnKey] = obj;

      const key = obj.categoryId || obj.category || 'default';

      let groupItem = groupByKey[key];
      if (!groupItem) {
        groupItem = {
          key: key,
          columns: [],
          groupColumns: [],
          category: obj.category,
        };
        groupByKey[key] = groupItem;
        groupList.push(groupItem);
      }
      groupItem.columns.push(obj);
      if (!obj.disabledGroupColumn) {
        groupItem.groupColumns.push(obj);
      }
    }
    setColumnCategories(
      groupList.filter((item) => {
        return item.columns.length > 0;
      })
    );

    setColumnsByKey(columnMap);
  }, []);

  const initializeSelection = useCallback((filterColumns: ColumnDefinition[], columnsByKey: Record<string, ColumnDefinition>) => {
    /* eslint-disable @typescript-eslint/no-explicit-any */
    const selectedList: any[] = [];

    let selectedGroup: any[] = [],
      selectedBucket: string | null = null;
    const selectedAggregateMetrics: any[] = [];
    let bucketConfig: Record<string, any> = {};
    

    const { groupColumn, groupColumns, metricData } = getGroupColumnData(
      filterColumns
    );

    console.log("initializeSelection - groupColumns:", groupColumns);

    if (groupColumns && groupColumns.length > 0) {
      // Support multiple group columns
      selectedGroup = groupColumns
        .map((gc: any) => {
          const obj = columnsByKey[gc.columnKey];
          return obj ? transformItemByKey(gc.columnKey, obj) : null;
        })
        .filter((item: any) => item !== null);

      metricData.forEach((metric: any) => {
        const metricColumn = filterColumns.find(
          (col) =>
            col.aggregateColumn === metric.aggregateColumn &&
            !col.aggregationType
        );
        if (metricColumn) {
          selectedAggregateMetrics.push(
            getAggregateMetricsColumn(
              metricColumn.columnKey + "." + metric.aggregationType.toLowerCase(),
              metricColumn,
              translate
            )
          );
        }
      });

      selectedBucket = groupColumn?.bucketType ?? null;
      bucketConfig = groupColumn?.bucketConfig || {};
    } else if (groupColumn) {
      // Backwards compatibility: single group column
      const columnKey = groupColumn.columnKey;
      const obj = columnsByKey[columnKey];
      if (obj) {
        selectedGroup = [transformItemByKey(columnKey, obj)];
      }

      metricData.forEach((metric: any) => {
        const metricColumn = filterColumns.find(
          (col) =>
            col.aggregateColumn === metric.aggregateColumn &&
            !col.aggregationType
        );
        if (metricColumn) {
          selectedAggregateMetrics.push(
            getAggregateMetricsColumn(
              metricColumn.columnKey + "." + metric.aggregationType.toLowerCase(),
              metricColumn,
              translate
            )
          );
        }
      });

      selectedBucket = groupColumn.bucketType ?? null;
      bucketConfig = groupColumn.bucketConfig || {};
    }

    // dont show columns in group
    const columns = filterColumns.filter((col) => !col.aggregationType);

    for (let i = 0; i < columns.length; i++) {
      const columnKey = columns[i].columnKey;
      const obj = columnsByKey[columnKey];

      // should not happen !
      if (!obj) continue;
      selectedList.push(transformItemByKey(columnKey, obj));
    }

    setConfigState({
      selectedColumns: selectedList,
      selectedGroup: selectedGroup,
      selectedBucket: selectedBucket,
      bucketConfig: bucketConfig,
      selectedAggregateMetrics: selectedAggregateMetrics,
    });
  }, []);

  useEffect(() => {
    initializeCategories(allColumns);
  }, [allColumns, initializeCategories]);

  useEffect(() => {
    console.log(
      "SntConfigColumns - filter.columns:",
      filter.columns?.map((c: ColumnDefinition) => ({
        key: c.key,
        columnKey: c.columnKey,
        isGrouped: c.isGrouped,
        aggregationType: c.aggregationType,
      }))
    );
    initializeSelection(filter.columns, columnsByKey);
  }, [filter.columns, columnsByKey, initializeSelection]);

  useEffect(() => {
    console.log(
      "SntConfigColumns - dialog opened, isShowConfigDialog:",
      isShowConfigDialog
    );
    console.log(
      "SntConfigColumns - filter.columns when dialog opens:",
      filter.columns?.map((c: ColumnDefinition) => ({
        key: c.key,
        columnKey: c.columnKey,
        isGrouped: c.isGrouped,
        aggregationType: c.aggregationType,
      }))
    );
    setSearchColumn("");
    if (filter && filter.sortBy) {
      setSortBy({
        key: filter.sortBy.column.sortKey,
        desc: filter.sortBy.desc,
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isShowConfigDialog]);

  const onResetDefault = () => {
    if (defaultVisibleColumnKeys) {
      const cols = defaultVisibleColumnKeys
        .map((key: string) => {
          const obj = columnsByKey[key];
          if (!obj) {
            return null;
          }

          return transformItemByKey(key, obj);
        })
        .filter((item: any) => item !== null);

      if(configState){
        setConfigState({
          ...configState,
          selectedColumns: cols,
          selectedGroup: [],
          selectedBucket: null,
          bucketConfig: {},
        });
      }      

      if (defaultSortBy?.length) {
        setSortBy({ key: defaultSortBy[0], desc: defaultSortBy[1] });
      } else if (cols.length > 0) {
        setSortBy({ key: cols[0]?.key || "", desc: false });
      }
    }
  };

  if (!configState) return <></>;

  return (
    <SntConfigColumns1
      isShowConfigDialog={isShowConfigDialog}
      onDone={onDone}
      onCancel={onCancel}
      isDisabbledGroup={isDisabbledGroup}
      isDisabbledUnGroupIcon={isDisabbledUnGroupIcon}
      isMobile={isMobile}
      configState={configState}
      onConfigStateChange={(data) => {
        setConfigState({ ...configState, ...data });
      }}
      filterQuery={filterQuery} // for checking
      columnCategories={columnCategories}
      columnsByKey={columnsByKey}
      setColumnsSetting={setColumnsSetting}
      translate={translate}
      sortBy={sortBy}
      onSortByChange={setSortBy}
      isHiddenSortIcons={isHiddenSortIcons}
      searchColumn={searchColumn}
      onSearchChange={setSearchColumn}
      isAggregate={hasFeature(FeatureKey.search_aggregate)}
      isSupportedMetrics={
        isHiddenSupportedMetrics === true
          ? false
          : hasFeature(FeatureKey.table_advanced_aggregations)
      }
      onResetDefault={onResetDefault}
    />
  );
};

/* eslint-disable @typescript-eslint/no-explicit-any */
interface SntConfigColumns1Props {
  isShowConfigDialog: boolean;
  onDone: () => void;
  onCancel: () => void;
  configState: ConfigState | null;
  onConfigStateChange: (state: ConfigState) => void;
  isDisabbledGroup?: boolean;
  isDisabbledUnGroupIcon?: boolean;
  columnCategories: ColumnCategory[];
  columnsByKey: Record<string, ColumnDefinition>;
  filterQuery: any[];
  /* eslint-disable @typescript-eslint/no-explicit-any */
  setColumnsSetting: (list: any[], sortBy?: any, groupKeys?: any[], selectedBucket?: any, bucketConfig?: any, metrics?: any[]) => void;
  
  translate: (key: LanguageKey) => string;
  isMobile: boolean;
  sortBy: {key: string; desc?: boolean} | undefined;
  onSortByChange?: (sortBy: {key: string; desc?: boolean}) => void;
  isHiddenSortIcons?: boolean;
  isHideAdvancedAggregations?: boolean;
  onResetDefault?: () => void;
}

export const SntConfigColumns1 = ({
  isShowConfigDialog,
  onDone,
  onCancel,

  configState,
  onConfigStateChange,

  isDisabbledGroup,
  isDisabbledUnGroupIcon = false,

  columnCategories,
  columnsByKey,

  filterQuery,
  setColumnsSetting,

  translate,
  isMobile,

  sortBy,
  onSortByChange,
  isHiddenSortIcons = false,
  searchColumn,
  onSearchChange,
  isAggregate,
  isSupportedMetrics,
  onResetDefault,
}: SntConfigColumns1Props & {
  searchColumn?: string;
  onSearchChange?: (search: string) => void;
  isAggregate?: boolean;
  isSupportedMetrics?: boolean;
}) => {
  const ref = useRef<HTMLDivElement>(null);

  const [buckets] = useState([
    {
      id: bucketTypeConstant.DAY,
      label: translate(LanguageKey.by_day_key),
    },
    {
      id: bucketTypeConstant.WEEK,
      label: translate(LanguageKey.by_week_key),
    },
    {
      id: bucketTypeConstant.MONTH,
      label: translate(LanguageKey.by_month_key),
    },
    {
      id: bucketTypeConstant.YEAR,
      label: translate(LanguageKey.by_year_key),
    },
  ]);

  const {
    selectedColumns,
    selectedGroup,
    selectedBucket,
    bucketConfig,
    selectedAggregateMetrics,
  } = configState || {
    selectedColumns: [],
    selectedGroup: [],
    selectedBucket: null,
    bucketConfig: {},
    selectedAggregateMetrics: [],
  };

  const onSaveSettingsPopup = () => {
    /* eslint-disable @typescript-eslint/no-explicit-any */
    let list = selectedColumns.map((item: any) => item.key);
    const groupKeys = [],
      metrics = [];

    if (selectedGroup.length > 0) {
      // Pass all group keys, not just the first one
      groupKeys.push(...selectedGroup.map((g: any) => g.key));
    

      if (selectedGroup[0].supportedGroupType === "NUMERIC_BUCKET") {
        if (
          selectedBucket === bucketTypeConstant.WIDTH &&
          (!bucketConfig.low ||
            !bucketConfig.high ||
            !bucketConfig.division ||
            bucketConfig.low > bucketConfig.high)
        ) {
          onBucketConfigChange({ ...bucketConfig, isSubmited: true });
          return;
        }
      }
      // Add all group keys to the list
      list = [...groupKeys, ...list];
      /* eslint-disable @typescript-eslint/no-explicit-any */
      metrics.push(...selectedAggregateMetrics.map(
        (metric: any) =>
          metric.columnMetricKey + "." + metric.aggregationType.toLowerCase()
      ));
      
    }
    setColumnsSetting(
      list,
      sortBy ? { key: sortBy.key, desc: sortBy.desc } : undefined,
      groupKeys,
      selectedBucket,
      bucketConfig,
      [...new Set(metrics)]
    );

    onDone();
  };

  const onCloseDialog = () => {
    onCancel();
  };

  const onSelectedColumn = (key: string) => {
    const obj = columnsByKey[key];
    if (selectedColumns.length === 0) {
      // if current selectedColumn length = 0, reset sort
      onSortByChange?.({ ...sortBy, key: key });
    }
    if (configState) {
      onConfigStateChange?.({
        ...configState,
        selectedColumns: [...selectedColumns, transformItemByKey(key, obj)],
      });
    }
  };
  const onUnSelectedColumn = (key: string) => {
    /* eslint-disable @typescript-eslint/no-explicit-any */
    const list = selectedColumns.filter((_key: any) => _key.key !== key);
    
    if (configState) {
      onConfigStateChange?.({
        ...configState,
        selectedColumns: list,
      });
    }

    // remove current key has sort, should reset sort
    if (sortBy && sortBy.key === key && list.length) {
      onSortByChange?.({ ...sortBy, key: list[0].key });
    }
  };

  const onSelectedGroup = (key: string) => {
    const obj = columnsByKey[key];

    let selectedBucketType = null;
    if (obj.supportedGroupType === supportedGroupTypeConstant.DATE_BUCKET) {
      selectedBucketType = bucketTypeConstant.DAY;
    } else if (
      obj.supportedGroupType === supportedGroupTypeConstant.NUMERIC_BUCKET
    ) {
      selectedBucketType = bucketTypeConstant.AUTO_WIDTH;
    } else if (
      obj.supportedGroupType === supportedGroupTypeConstant.TAG_BASED
    ) {
      selectedBucketType = bucketTypeConstant.TAGS_FULL;
    }

    let metrics = selectedAggregateMetrics;
    if (!metrics.length || !isSupportedMetrics) {
      metrics = obj.supportedAggregateMetrics?.map((metric: any) =>
        getAggregateMetricsColumn(
          key + "." + metric.toLowerCase(),
          obj,
          translate
        )
      ) || [];
    }

    if (configState) {
      onConfigStateChange?.({
        ...configState,
        selectedGroup: [transformItemByKey(key, obj)],
        selectedAggregateMetrics: metrics,
        selectedBucket: selectedBucketType,
        bucketConfig: {},
      });
    }
    if (sortBy) {
      onSortByChange?.({ ...sortBy, key: key });
    }
  };
  
  const onUnSelectedGroup = (key: string) => {
    // Remove the specific group from the array
    /* eslint-disable @typescript-eslint/no-explicit-any */
    const newGroups = selectedGroup.filter((g: any) => g.key !== key);
    

    if (newGroups.length === 0) {
      // If no groups left, clear everything
      if (sortBy) {
        onSortByChange?.({
          ...sortBy,
          key: selectedColumns.length ? selectedColumns[0].key : null,
        });
      }
      if (configState) {
        onConfigStateChange?.({
          ...configState,
          selectedGroup: [],
          selectedAggregateMetrics: [],
          selectedBucket: null,
          bucketConfig: {},
        });
      }
    } else {
      // If there are still groups, just update the array
      if (configState) {
        onConfigStateChange?.({
          ...configState,
          selectedGroup: newGroups,
        });
      }
      // Update sort to first remaining group if current sort was the removed group
      if (sortBy && sortBy.key === key) {
        onSortByChange?.({ ...sortBy, key: newGroups[0].key });
      }
    }
  };

  const renderAvailableSection = () => {
    return availableSection(
      columnCategories,
      selectedColumns,
      onSelectedColumn
    );
  };

  const onSelectedBucketChange = (selectedBucketType: any) => {
    if (configState) {
      onConfigStateChange?.({
        ...configState,
        selectedBucket: selectedBucketType,
      });
    }
  };

  const onBucketConfigChange = (bucketConfigUpdate: any) => {
    if (configState) {
      onConfigStateChange?.({
        ...configState,
        bucketConfig: bucketConfigUpdate,
      });
    }
  };

  const onSelectedMetricColumn = (key: string) => {
    const obj = columnsByKey[key];
    const firstMetric = obj.supportedAggregateMetrics?.[0];
    const metric = getAggregateMetricsColumn(
      key + "." + firstMetric?.toLowerCase(),
      obj,
      translate
    );

    if (configState) {
      onConfigStateChange?.({
        ...configState,
        selectedAggregateMetrics: [...selectedAggregateMetrics, metric],
      });
    }
  };

  const onUnSelectedMetricColumn = (key: string) => {
    /* eslint-disable @typescript-eslint/no-explicit-any */
    const list = selectedAggregateMetrics.filter((_key: any) => _key.key !== key);
    
    if (configState) {
      onConfigStateChange?.({
        ...configState,
        selectedAggregateMetrics: list,
      });
    }

    // remove current key has sort, should reset sort
    if (sortBy && sortBy.key === key && list.length) {
      onSortByChange?.({ ...sortBy, key: list[0].key });
    }
  };

  /* eslint-disable @typescript-eslint/no-explicit-any */
  const onMetricTypeChanged = (index: any, item: any, data: any) => {
    const updatedList = selectedAggregateMetrics.map((metric: any, idx: any) => {
      if (idx === index) {
        const updateKey = metric.columnMetricKey + "." + data.value.toLowerCase();

        if (sortBy && metric.key === sortBy.key) {
          onSortByChange?.({ ...sortBy, key: updateKey });
        }
        return {
          ...metric,
          aggregationType: data.value,
          key: updateKey,
          sortKey: updateKey,
        };
      }
      return metric;
    });
    if (configState) {
      onConfigStateChange?.({
        ...configState,
        selectedAggregateMetrics: updatedList,
      });
    }
  
  };

  /* eslint-disable @typescript-eslint/no-explicit-any */
  const availableSection = (categories: any, selected: any, onClick: any) => {
  
    return (
      <div
        className="column-scroll snt-scroll"
        style={{ maxHeight: isMobile ? "40vh" : "70vh" }}
      >
        {/* eslint-disable @typescript-eslint/no-explicit-any */}
        {categories.map((item: any, index: any) => {
          let _showGroup = false;

          const _listItems = item.columns.map((obj: any, idx: any) => {
            const _title = obj.settingTitle || obj.title || "";
            let isShowItem = false;
            if (
              _title.toLowerCase().indexOf((searchColumn || '').toLowerCase()) > -1 &&
              selected.filter((item: any) => item.key === obj.columnKey).length === 0
            ) {
              _showGroup = true;
              isShowItem = true;
            }

            if (!isShowItem) return <span key={idx}></span>;
            return (
              <li
                key={idx}
                className="list-group-item list-group-item-unselected"
              >
                <Row>
                  <Col xs={6} xl={3}>
                    <SntCheckBox
                      checked={false}
                      onChange={() => {
                        onClick(obj.columnKey);
                      }}
                      label={
                        <span className="ps-1">
                          {obj.settingTitle || obj.title}
                          <SensolusOnlyBadge systemOnly={obj.systemOnly} />
                        </span>
                      }
                    />
                  </Col>
                  <Col xs={6} xl={8}>
                    {obj.description &&
                      obj.description !== ((obj as any).settingTitle || obj.title) && (
                        <div
                          className="d-flex"
                          style={{
                            color: "#424B59",
                          }}
                        >
                          <div style={{ minWidth: 24 }}>
                            <IcomoonIcon icon="info" size={18} />
                          </div>
                          <div className="flex-grow-1">{obj.description}</div>
                        </div>
                      )}
                  </Col>
                </Row>
              </li>
            );
          });

          return (
            <div
              key={index}
              className="mb-3"
              style={{ display: _showGroup ? "block" : "none" }}
            >
              {item.category && (
                <label className="fw-bold">{item.category}</label>
              )}
              <ul className="list-group caterogy-group">{_listItems}</ul>
            </div>
          );
        })}
      </div>
    );
  };

  const renderSelectedSection = () => {
    return columnsSelectedSection();
  };

  const columnsSelectedSection = () => {
    return (
      <div
        className="column-scroll snt-scroll"
        style={{ maxHeight: isMobile ? "40vh" : "70vh", paddingBottom: 15 }}
      >
        <Row>
          <Col>
            <span className="fw-bold">{selectedColumns.length}</span>{" "}
            {translate(LanguageKey.columns_selected_key)}
          </Col>
          <Col>
            <SntLink
              className="float-end"
              onClick={() => {
                onResetDefault?.();
              }}
            >
              {translate(LanguageKey.reset_to_defaults_key)}
            </SntLink>
          </Col>
        </Row>
        <div className="mb-2">{translate(LanguageKey.columns_selected_info_key)}</div>
        {groupSelectedList(selectedGroup, onUnSelectedGroup)}

        {selectedGroup.length ? (
          <>
            <div className="mb-2 fw-bold">{"Metrics"}</div>
            <div className="mb-4">
              {metricSection(
                selectedAggregateMetrics,
                (data: any) => {
                  if (configState) {
                    onConfigStateChange?.({
                      ...configState,
                      selectedAggregateMetrics: data,
                    });
                  }
                },
                onUnSelectedMetricColumn
              )}
            </div>

            <div
              className="d-flex mb-2"
              style={{
                color: "#424B59",
              }}
            >
              <div style={{ minWidth: 24 }}>
                <IcomoonIcon icon="info" size={18} />
              </div>
              <div className="flex-grow-1">
                {translate(LanguageKey.columns_are_not_visible_info_key)}
              </div>
            </div>
          </>
        ) : (
          <></>
        )}

        {selectedSection(
          selectedColumns,
          (data: any) => {
            if (configState) {
              onConfigStateChange?.({
                ...configState,
                selectedColumns: data,
              });
            }
          },
          onUnSelectedColumn
        )}
      </div>
    );
  };

  /* eslint-disable @typescript-eslint/no-explicit-any */
  const selectedSection = (selected: any, setSelected: any, onClick: any) => {
  
    return (
      <div
        className="column-scroll snt-scroll"
        style={{ maxHeight: isMobile ? "40vh" : "70vh" }}
      >
        <ReactSortable
          animation={150}
          list={selected}
          setList={setSelected}
          tag="ul"
          className="list-group caterogy-group"
        >
          {selected.map((item: any, index: any) => {
            return (
              <li
                key={index}
                className={`list-group-item list-group-item-selected`}
              >
                <Row>
                  <Col xs={10} md={7}>
                    <div>
                      {item.Header}
                      <SensolusOnlyBadge systemOnly={item.systemOnly} />
                    </div>
                  </Col>
                  <Col xs={2} md={5}>
                    <div
                      style={{
                        position: "absolute",
                        top: "50%",
                        right: "1%",
                        transform: "translate(0%, -50%)",
                      }}
                    >
                      {!isHiddenSortIcons && !item.disableSortBy && !selectedGroup.length && (
                        <>
                          {sortBy?.key === item.sortKey ? (
                            sortBy?.desc ? (
                              <SntSortZA
                                className="me-2"
                                onClick={() => {
                                  onSortByChange?.({ key: sortBy?.key, desc: false });
                                }}
                              />
                            ) : (
                              <SntSortAZ
                                className="me-2"
                                onClick={() => {
                                  onSortByChange?.({ key: sortBy?.key ?? '', desc: true });
                                }}
                              />
                            )
                          ) : (
                            <SntSortAZ
                              className="me-2 sortIconHover"
                              onClick={() => {
                                onSortByChange?.({
                                  key: item.sortKey,
                                  desc: false,
                                });
                              }}
                            />
                          )}
                        </>
                      )}
                      {isAggregate && !isDisabbledGroup && (
                        <>
                          {isSupportedMetrics && selectedGroup.length ? (
                            <SntButton
                              title={translate(LanguageKey.add_to_metrics)}
                              size="sm"
                              className={"me-2"}
                              text={"f(x)"}
                              style={{ padding: "0.15rem" }}
                              onClick={() => {
                                onSelectedMetricColumn(item.key);
                              }}
                            />
                          ) : (
                            <></>
                          )}

                          <SntGroupBy
                            className={"me-2"}
                            title={translate(LanguageKey.group_on_column)}
                            style={{
                              visibility:
                                item.disabledGroupColumn ||
                                (selectedGroup.length &&
                                  selectedGroup[0].key === item.key)
                                  ? "hidden"
                                  : "unset",
                            }}
                            onClick={() => {
                              if (!item.disabledGroupColumn) {
                                onSelectedGroup(item.key);
                              }
                            }}
                          />
                        </>
                      )}
                      <IcomoonIcon
                        style={{
                          visibility: !item.description ? "hidden" : "unset",
                        }}
                        icon="info"
                        className={"me-2"}
                        size={18}
                        title={item.description}
                      />
                      <SntReorderIcon
                        title={translate(LanguageKey.drag_drop_order_key)}
                        className={"me-2"}
                        style={{ color: "#b9c0ca" }}
                      />
                      <SntCloseIcon
                        title={translate(LanguageKey.remove_column_key)}
                        className={"me-2"}
                        onClick={() => {
                          onClick(item.key);
                        }}
                      />
                    </div>
                  </Col>
                </Row>
              </li>
            );
          })}
        </ReactSortable>
      </div>
    );
  };

  /* eslint-disable @typescript-eslint/no-explicit-any */
  const metricSection = (selected: any, setSelected: any, onClick: any) => {
  
    return (
      <div
        className="column-scroll snt-scroll"
        style={{ maxHeight: isMobile ? "40vh" : "70vh" }}
      >
        <ReactSortable
          animation={150}
          list={selected}
          setList={setSelected}
          tag="ul"
          className="list-group caterogy-group"
        >
          {selected.map((item: any, index: any) => {
            return (
              <li
                key={index}
                className={`list-group-item list-group-item-selected`}
              >
                <Row>
                  <Col xs={10} md={10}>
                    <div className="d-flex align-items-center">
                      <div className="flex-shrink-0" style={{ width: 30 }}>
                        {"f(x)"}
                      </div>
                      <div
                        className="flex-shrink-0 pe-2"
                        style={{ width: 110 }}
                      >
                        <SntSelect
                          value={item.options?.filter(
                            (option: any) => option.value === item.aggregationType
                          )}
                          options={item.options}
                          isDisabled={!isSupportedMetrics}
                          onChange={(data: any) => {
                            onMetricTypeChanged(index, item, data);
                          }}
                        />
                      </div>
                      <div
                        className="flex-grow-1 text-truncate pe-2"
                        title={item.Header}
                      >
                        {item.Header}
                      </div>
                    </div>
                  </Col>
                  <Col xs={2} md={2}>
                    <div
                      style={{
                        position: "absolute",
                        top: "50%",
                        right: "1%",
                        transform: "translate(0%, -50%)",
                      }}
                    >
                      {!isHiddenSortIcons && !item.disableSortBy && (
                        <>
                          {sortBy?.key === item.sortKey ? (
                            sortBy?.desc ? (
                              <SntSortZA
                                className="me-2"
                                onClick={() => {
                                  onSortByChange?.({ key: sortBy?.key, desc: false });
                                }}
                              />
                            ) : (
                              <SntSortAZ
                                className="me-2"
                                onClick={() => {
                                  onSortByChange?.({ key: sortBy?.key ?? '', desc: true });
                                }}
                              />
                            )
                          ) : (
                            <SntSortAZ
                              className="me-2 sortIconHover"
                              onClick={() => {
                                onSortByChange?.({
                                  key: item.sortKey,
                                  desc: false,
                                });
                              }}
                            />
                          )}
                        </>
                      )}
                      {selected.length > 1 ? (
                        <>
                          <SntReorderIcon
                            title={translate(LanguageKey.drag_drop_order_key)}
                            className={"me-2"}
                            style={{ color: "#b9c0ca" }}
                          />
                          <SntCloseIcon
                            title={translate(LanguageKey.remove_column_key)}
                            className={"me-2"}
                            onClick={() => {
                              onClick(item.key);
                            }}
                          />
                        </>
                      ) : (
                        <></>
                      )}
                    </div>
                  </Col>
                </Row>
              </li>
            );
          })}
        </ReactSortable>
      </div>
    );
  };

  const getValidLowClass = () => {
    if (
      !bucketConfig.isSubmited &&
      !Object.keys(bucketConfig).includes("low")
    ) {
      return "";
    }
    if (
      !bucketConfig.low ||
      (bucketConfig.high && bucketConfig.low > bucketConfig.high)
    ) {
      return "is-invalid";
    }
    return "is-valid";
  };

  const getValidHighClass = () => {
    if (
      !bucketConfig.isSubmited &&
      !Object.keys(bucketConfig).includes("high")
    ) {
      return "";
    }
    if (
      !bucketConfig.high ||
      (bucketConfig.low && bucketConfig.low > bucketConfig.high)
    ) {
      return "is-invalid";
    }
    return "is-valid";
  };

  const getValidDivisionClass = () => {
    if (
      !bucketConfig.isSubmited &&
      !Object.keys(bucketConfig).includes("division")
    ) {
      return "";
    }
    if (!bucketConfig.division) {
      return "is-invalid";
    }
    return "is-valid";
  };

  /* eslint-disable @typescript-eslint/no-explicit-any */
  const getSupportedGroupUnits = (column: any) => {
  
    const results: any[] = [];
    if (
      column &&
      column.supportedGroupUnits &&
      column.supportedGroupUnits.length > 0
    ) {
      column.supportedGroupUnits?.forEach((unit: any) =>
        results.push({
          value: unit,
          label: unit, // Simplified to avoid const enum access issue
        })
      );
    }

    return results;
  };

  const groupSelectedList = (selected: any, onClick: any) => {
    return (
      <ul className="list-group caterogy-group">
        {selected.map((item: any, index: any) => {
          const supportedGroupUnits = getSupportedGroupUnits(item);
          return (
            <div key={index}>
              <li className="list-group-item list-group-item-selected group">
                <Row>
                  <Col xs={10} md={7}>
                    <div>
                      {item.Header}{" "}
                      {!item.disabledGroupColumn && (
                        <span className="fw-bold"> - grouped</span>
                      )}
                    </div>
                  </Col>
                  <Col xs={2} md={5}>
                    <div
                      style={{
                        position: "absolute",
                        top: "50%",
                        right: "1%",
                        transform: "translate(0%, -50%)",
                      }}
                    >
                      <>
                        {!item.disableSortBy && (
                          <>
                            {sortBy?.key === item.sortKey ? (
                              sortBy?.desc ? (
                                <SntSortZA
                                  className="me-2"
                                  onClick={() => {
                                    onSortByChange?.({ key: sortBy?.key, desc: false });
                                  }}
                                />
                              ) : (
                                <SntSortAZ
                                  className="me-2"
                                  onClick={() => {
                                    onSortByChange?.({ key: sortBy?.key ?? '', desc: true });
                                  }}
                                />
                              )
                            ) : (
                              <SntSortAZ
                                className="me-2 sortIconHover"
                                onClick={() => {
                                  onSortByChange?.({
                                    key: item.sortKey,
                                    desc: false,
                                  });
                                }}
                              />
                            )}
                          </>
                        )}
                        {!isDisabbledUnGroupIcon && (
                          <SntUnGroup
                            title={translate(LanguageKey.ungroup_on_column)}
                            className={"me-2"}
                            style={{
                              visibility: item.disabledGroupColumn
                                ? "hidden"
                                : "unset",
                            }}
                            onClick={() => {
                              onClick(item.key);
                            }}
                          />
                        )}
                      </>

                      <IcomoonIcon
                        style={{
                          visibility: !item.description ? "hidden" : "unset",
                        }}
                        icon="info"
                        className={"me-2"}
                        size={18}
                        title={item.description}
                      />
                    </div>
                  </Col>
                </Row>
              </li>
              {item.supportedGroupType ===
                supportedGroupTypeConstant.DATE_BUCKET && (
                <li className="list-group-item list-group-item-selected group">
                  <div>{translate(LanguageKey.select_time_window_key)}</div>
                  <VerticalLayout>
                    {buckets.map((bucket: any) => {
                      return (
                        <SntRadio
                          key={bucket.id}
                          value={bucket.id}
                          selectedValue={selectedBucket || undefined}
                          onChange={() => {
                            onSelectedBucketChange(bucket.id);
                          }}
                          label={bucket.label}
                        />
                      );
                    })}
                  </VerticalLayout>
                </li>
              )}

              {item.supportedGroupType ===
                supportedGroupTypeConstant.NUMERIC_BUCKET && (
                <li className="list-group-item list-group-item-selected group">
                  <div>{translate(LanguageKey.choose_histogram_type)}</div>
                  <VerticalLayout>
                    <SntRadio
                      key={bucketTypeConstant.AUTO_WIDTH}
                      value={bucketTypeConstant.AUTO_WIDTH}
                      selectedValue={selectedBucket || undefined}
                      onChange={() => {
                        onSelectedBucketChange(bucketTypeConstant.AUTO_WIDTH);
                      }}
                      label={translate(LanguageKey.automatic_key)}
                    />
                    <SntRadio
                      key={bucketTypeConstant.WIDTH}
                      value={bucketTypeConstant.WIDTH}
                      selectedValue={selectedBucket || undefined}
                      onChange={() => {
                        onSelectedBucketChange(bucketTypeConstant.WIDTH);
                      }}
                      label={translate(LanguageKey.manual)}
                    />
                  </VerticalLayout>
                  {selectedBucket === bucketTypeConstant.WIDTH && (
                    <div>
                      {item &&
                        item.supportedGroupUnits &&
                        item.supportedGroupUnits.length > 0 && (
                          <Row>
                            <Col xs="5" lg="5">
                              <Form.Label
                                style={{
                                  fontWeight: "normal",
                                  paddingLeft: "32px",
                                }}
                                className="mt-2"
                              >
                                {translate(LanguageKey.trackers_key)}
                              </Form.Label>
                            </Col>
                            <Col xs="7" lg="7">
                              <SntSelect
                                value={supportedGroupUnits.filter(
                                  (option: any) =>
                                    option.value ===
                                    (bucketConfig.unit ||
                                      supportedGroupUnits[0].value)
                                )}
                                options={supportedGroupUnits}
                                onChange={(unit: any) => {
                                  onBucketConfigChange({
                                    ...bucketConfig,
                                    unit: unit.value,
                                  });
                                }}
                              />
                            </Col>
                          </Row>
                        )}
                      <Row>
                        <Col xs="5" lg="5">
                          <Form.Label
                            style={{
                              fontWeight: "normal",
                              paddingLeft: "32px",
                            }}
                            className="mt-2"
                          >
                            {translate(LanguageKey.minimum_value_key)}
                          </Form.Label>
                        </Col>
                        <Col xs="7" lg="7">
                          <Form.Control
                            type="number"
                            min="0"
                            value={bucketConfig.low}
                            onChange={(e) =>
                              onBucketConfigChange({
                                ...bucketConfig,
                                low: e.target.value,
                              })
                            }
                            className={`${getValidLowClass()}`}
                            size={"sm" as any}
                          />
                          <Form.Control.Feedback type="invalid">
                            The min value should be less than or equal max value
                          </Form.Control.Feedback>
                        </Col>
                      </Row>
                      <Row>
                        <Col xs="5" lg="5">
                          <Form.Label
                            style={{
                              fontWeight: "normal",
                              paddingLeft: "32px",
                            }}
                            className="mt-2"
                          >
                            {translate(LanguageKey.maximum_value_key)}
                          </Form.Label>
                        </Col>
                        <Col xs="7" lg="7">
                          <Form.Control
                            type="number"
                            min="0"
                            value={bucketConfig.high}
                            onChange={(e) =>
                              onBucketConfigChange({
                                ...bucketConfig,
                                high: e.target.value,
                              })
                            }
                            className={`${getValidHighClass()}`}
                            size={"sm" as any}
                          />
                          <Form.Control.Feedback type="invalid">
                            The max value should be greater than or equal min
                            value
                          </Form.Control.Feedback>
                        </Col>
                      </Row>
                      <Row>
                        <Col xs="5" lg="5">
                          <Form.Label
                            style={{
                              fontWeight: "normal",
                              paddingLeft: "32px",
                            }}
                            className="mt-2"
                          >
                            {translate(LanguageKey.divisions_up_to_20_key)}
                          </Form.Label>
                        </Col>
                        <Col xs="7" lg="7">
                          <Form.Control
                            type="number"
                            min={1}
                            max={20}
                            value={bucketConfig.division}
                            onChange={(e) => {
                              const newValue = Math.max(parseInt(e.target.value), 1);
                              const finalValue = Math.min(newValue, 20);
                              onBucketConfigChange({
                                ...bucketConfig,
                                division: finalValue,
                              });
                            }}
                            size={"sm" as any}
                            className={`${getValidDivisionClass()}`}
                          />
                          <Form.Control.Feedback type="invalid">
                            This field is required
                          </Form.Control.Feedback>
                        </Col>
                      </Row>
                    </div>
                  )}
                </li>
              )}

              {filterQuery.find(
                (q) =>
                  q.filterKey === item?.redirectFilter?.key &&
                  q.filterValue?.selectedValues?.length
              ) &&
                item.supportedGroupType ===
                  supportedGroupTypeConstant.TAG_BASED && (
                  <li className="list-group-item list-group-item-selected group">
                    <div>{translate(LanguageKey.use_all_tag_when_grouping)}</div>
                    <VerticalLayout>
                      <SntRadio
                        key={bucketTypeConstant.TAGS_FULL}
                        value={bucketTypeConstant.TAGS_FULL}
                        selectedValue={selectedBucket || undefined}
                        onChange={() => {
                          onSelectedBucketChange(bucketTypeConstant.TAGS_FULL);
                        }}
                        label={translate(LanguageKey.yes_default)}
                      />
                      <SntRadio
                        key={bucketTypeConstant.TAGS_FILTERED}
                        value={bucketTypeConstant.TAGS_FILTERED}
                        selectedValue={selectedBucket || undefined}
                        onChange={() => {
                          onSelectedBucketChange(
                            bucketTypeConstant.TAGS_FILTERED
                          );
                        }}
                        label={translate(LanguageKey.no_only_filtered)}
                      />
                    </VerticalLayout>
                  </li>
                )}
            </div>
          );
        })}
      </ul>
    );
  };

  return (
    <SntDialog
      isShow={isShowConfigDialog}
      onSave={onSaveSettingsPopup}
      onClose={onCloseDialog}
      onHide={onSaveSettingsPopup}
      title={translate(LanguageKey.config_columns_key)}
      saveTxt={translate(LanguageKey.ok_key)}
      closeTxt={translate(LanguageKey.cancel_key)}
      refWrapper={ref}
      customModalClassname={"snt-config-column-modal"}
      bodyClass={"p-0"}
    >
      {() => (
        <>
          <ConfigLayout className="table table-bordered">
            {isMobile ? (
              <tbody>
                <tr>
                  <td className="fw-bold">
                    {selectedGroup.length > 0
                      ? translate(LanguageKey.group_by_key)
                      : translate(LanguageKey.visible_columns)}
                  </td>
                </tr>
                <tr>
                  <td>{renderSelectedSection()}</td>
                </tr>
                <tr>
                  <td className="fw-bold">{translate(LanguageKey.available_columns_key)}</td>
                </tr>
                <tr>
                  <td>
                    <Form.Control
                      placeholder={translate(LanguageKey.search_column_key)}
                      value={searchColumn}
                      onChange={(e) => onSearchChange?.(e.target.value)}
                      size={"sm" as any}
                      className="mb-2"
                    />
                    {renderAvailableSection()}
                  </td>
                </tr>
              </tbody>
            ) : (
              <tbody>
                <tr>
                  <td className="fw-bold">
                    {selectedGroup.length > 0
                      ? translate(LanguageKey.group_by_key)
                      : translate(LanguageKey.visible_columns)}
                  </td>
                  <td className="fw-bold">{translate(LanguageKey.available_columns_key)}</td>
                </tr>
                <tr>
                  <td style={{ verticalAlign: "top" }}>
                    {renderSelectedSection()}
                  </td>
                  <td style={{ verticalAlign: "top" }}>
                    <Form.Control
                      placeholder={translate(LanguageKey.search_column_key)}
                      value={searchColumn}
                      onChange={(e) => onSearchChange?.(e.target.value)}
                      size={"sm" as any}
                      className="mb-2"
                    />
                    {renderAvailableSection()}
                  </td>
                </tr>
              </tbody>
            )}
          </ConfigLayout>
        </>
      )}
    </SntDialog>
  );
};

const getAggregateMetricsColumn = (key: string, groupColumn: ColumnDefinition, translate: (key: LanguageKey) => string) => {
  // Create a copy without isGrouped, bucketType, bucketConfig
  const {
    isGrouped,
    bucketType,
    bucketConfig,
    ...groupColumnWithoutGroupProps
  } = groupColumn;
  
  // Intentionally excluding these properties from the spread
  void isGrouped;
  void bucketType;
  void bucketConfig;

  const column = {
    ...groupColumnWithoutGroupProps,
    columnMetricKey: groupColumn.columnKey,
    key: key,
    sortKey: key,
    disabledGroupColumn: true,
    aggregateColumn: groupColumn.aggregateColumn,
    options: groupColumn.supportedAggregateMetrics?.map((key: any) => ({
      value: key,
      label: key,
    })),
  };
  if (key.includes(".count")) {
    return {
      ...column,
      aggregationType: "COUNT",
    };
  } else if (key.includes(".min")) {
    return {
      ...column,
      aggregationType: "MIN",
    };
  } else if (key.includes(".max")) {
    return {
      ...column,
      aggregationType: "MAX",
    };
  } else if (key.includes(".avg")) {
    return {
      ...column,
      aggregationType: "AVG",
    };
  } else {
    return column;
  }
};

const transformItemByKey = (columnKey: string, obj: ColumnDefinition) => {
  return {
    ...obj,
    key: columnKey,
    Header: obj.Header || obj.title || obj.description,
    disabledGroupColumn: obj.disabledGroupColumn,
    description:
      obj.description !== ((obj as any).settingTitle || obj.title)
        ? obj.description
        : null,
  };
};

export default SntConfigColumns;